import React, { useState, useEffect, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { SavedPresentation } from '@dopomogai/types';
import { DynamicRenderer } from './DynamicRenderer';

interface PresentationEngineProps {
  presentation: SavedPresentation;
  onExit: () => void;
}

export const PresentationEngine: React.FC<PresentationEngineProps> = ({ presentation, onExit }) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const { theme, slides } = presentation;
  const sortedSlides = useMemo(() => slides.sort((a, b) => a.order - b.order), [slides]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        setCurrentSlideIndex(s => Math.min(s + 1, sortedSlides.length - 1));
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setCurrentSlideIndex(s => Math.max(s - 1, 0));
      } else if (e.key === 'Escape') {
        onExit();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [sortedSlides.length, onExit]);

  const activeSlide = sortedSlides[currentSlideIndex];

  return (
    <div className="fixed inset-0 bg-black z-50 flex items-center justify-center font-sans">
      <AnimatePresence mode="wait">
        {activeSlide && (
          <motion.div
            key={activeSlide.id}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            className="w-full h-full flex items-center justify-center p-8"
            style={{ backgroundColor: theme.colors.tertiary }}
          >
            {/* We pass `data` and `theme` as props. The DynamicRenderer will forward them.*/}
            <DynamicRenderer
              code={activeSlide.content}
              data={activeSlide}
              theme={theme}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* UI Overlay */}
      <div className="absolute bottom-4 right-4 text-white/50 text-sm bg-black/20 px-2 py-1 rounded">
        {currentSlideIndex + 1} / {sortedSlides.length}
      </div>
      <button
        onClick={onExit}
        className="absolute top-4 right-4 text-white/50 hover:text-white bg-black/20 px-3 py-1 rounded-lg"
      >
        Exit (Esc)
      </button>
    </div>
  );
};